import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import './offers.css';

const Offers = () => {
  const { state, actions } = useDemo();
  const history = useHistory();
  const [selectedOffer, setSelectedOffer] = useState(null);

  const handleSelectOffer = (offerId) => {
    setSelectedOffer(offerId);
    actions.selectOffer(offerId);
  };

  const handleAcceptOffer = (offerId) => {
    actions.acceptOffer(offerId);
    actions.showToast('Offer accepted successfully! Redirecting to confirmation...');
    setTimeout(() => {
      history.push('/jo-xyz-demo/accept-offer');
    }, 1500);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const availableOffers = state.offers.filter(offer => offer.status === 'available');

  return (
    <div className="jo-xyz-demo-offers-page">
      <div className="jo-xyz-demo-offers-page__header">
        <h1>Lending Offers</h1>
        <p className="jo-xyz-demo-offers-page__subtitle">
          Compare and select from available lending offers based on your borrowing base
        </p>
      </div>

      {availableOffers.length === 0 ? (
        <div className="jo-xyz-demo-offers-page__empty">
          <div className="jo-xyz-demo-card">
            <div className="jo-xyz-demo-empty-state">
              <div className="jo-xyz-demo-empty-state__icon">📋</div>
              <h3>No Offers Available</h3>
              <p>No lending offers are currently available. Check back later or run the Opportunity Agent to generate new offers.</p>
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                onClick={() => history.push('/jo-xyz-demo/agents')}
              >
                Run Opportunity Agent
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Offers Summary */}
          <div className="jo-xyz-demo-offers-page__summary">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Offers Summary</h3>
                <p className="jo-xyz-demo-card__subtitle">
                  {availableOffers.length} offer{availableOffers.length !== 1 ? 's' : ''} available
                </p>
              </div>
              <div className="jo-xyz-demo-offers-summary">
                <div className="jo-xyz-demo-summary-stat">
                  <span className="jo-xyz-demo-summary-stat__label">Highest Amount</span>
                  <span className="jo-xyz-demo-summary-stat__value">
                    {formatCurrency(Math.max(...availableOffers.map(o => o.amount)))}
                  </span>
                </div>
                <div className="jo-xyz-demo-summary-stat">
                  <span className="jo-xyz-demo-summary-stat__label">Lowest Rate</span>
                  <span className="jo-xyz-demo-summary-stat__value">
                    {Math.min(...availableOffers.map(o => o.interestRate)).toFixed(2)}%
                  </span>
                </div>
                <div className="jo-xyz-demo-summary-stat">
                  <span className="jo-xyz-demo-summary-stat__label">Average Term</span>
                  <span className="jo-xyz-demo-summary-stat__value">
                    {Math.round(availableOffers.reduce((sum, o) => sum + o.term, 0) / availableOffers.length)} months
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Offers Grid */}
          <div className="jo-xyz-demo-offers-page__grid">
            <div className="jo-xyz-demo-grid jo-xyz-demo-grid--2">
              {availableOffers.map((offer) => {
                const daysUntilExpiry = getDaysUntilExpiry(offer.expiresAt);
                const isSelected = selectedOffer === offer.id;
                
                return (
                  <div 
                    key={offer.id} 
                    className={`jo-xyz-demo-offer-card ${isSelected ? 'jo-xyz-demo-offer-card--selected' : ''}`}
                    onClick={() => handleSelectOffer(offer.id)}
                  >
                    <div className="jo-xyz-demo-card">
                      <div className="jo-xyz-demo-offer-card__header">
                        <div className="jo-xyz-demo-offer-card__lender">
                          <h3 className="jo-xyz-demo-offer-card__lender-name">{offer.lender}</h3>
                          <div className={`jo-xyz-demo-offer-card__rating jo-xyz-demo-offer-card__rating--${offer.rating.toLowerCase()}`}>
                            {offer.rating}
                          </div>
                        </div>
                        <div className="jo-xyz-demo-offer-card__status">
                          {daysUntilExpiry > 0 ? (
                            <span className="jo-xyz-demo-offer-card__expiry">
                              Expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="jo-xyz-demo-offer-card__expired">Expired</span>
                          )}
                        </div>
                      </div>

                      <div className="jo-xyz-demo-offer-card__content">
                        <div className="jo-xyz-demo-offer-card__terms">
                          <div className="jo-xyz-demo-term">
                            <span className="jo-xyz-demo-term__label">Amount</span>
                            <span className="jo-xyz-demo-term__value">{formatCurrency(offer.amount)}</span>
                          </div>
                          <div className="jo-xyz-demo-term">
                            <span className="jo-xyz-demo-term__label">Interest Rate</span>
                            <span className="jo-xyz-demo-term__value">{offer.interestRate}%</span>
                          </div>
                          <div className="jo-xyz-demo-term">
                            <span className="jo-xyz-demo-term__label">Term</span>
                            <span className="jo-xyz-demo-term__value">{offer.term} months</span>
                          </div>
                          <div className="jo-xyz-demo-term">
                            <span className="jo-xyz-demo-term__label">Advance Rate</span>
                            <span className="jo-xyz-demo-term__value">{(offer.advanceRate * 100).toFixed(0)}%</span>
                          </div>
                        </div>

                        <div className="jo-xyz-demo-offer-card__fees">
                          <h4>Fees</h4>
                          <div className="jo-xyz-demo-fee-list">
                            <div className="jo-xyz-demo-fee">
                              <span className="jo-xyz-demo-fee__label">Origination</span>
                              <span className="jo-xyz-demo-fee__value">{(offer.fees.origination * 100).toFixed(2)}%</span>
                            </div>
                            <div className="jo-xyz-demo-fee">
                              <span className="jo-xyz-demo-fee__label">Annual</span>
                              <span className="jo-xyz-demo-fee__value">{(offer.fees.annual * 100).toFixed(2)}%</span>
                            </div>
                          </div>
                        </div>

                        <div className="jo-xyz-demo-offer-card__conditions">
                          <h4>Key Conditions</h4>
                          <ul className="jo-xyz-demo-conditions-list">
                            {offer.conditions.slice(0, 3).map((condition, index) => (
                              <li key={index} className="jo-xyz-demo-condition-item">
                                {condition}
                              </li>
                            ))}
                            {offer.conditions.length > 3 && (
                              <li className="jo-xyz-demo-condition-item jo-xyz-demo-condition-item--more">
                                +{offer.conditions.length - 3} more conditions
                              </li>
                            )}
                          </ul>
                        </div>

                        <div className="jo-xyz-demo-offer-card__actions">
                          <button 
                            className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptOffer(offer.id);
                            }}
                          >
                            Accept Offer
                          </button>
                          <div className="jo-xyz-demo-offer-card__funding-time">
                            Funding: {offer.fundingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Comparison Mode */}
          {selectedOffer && (
            <div className="jo-xyz-demo-offers-page__comparison">
              <div className="jo-xyz-demo-card">
                <div className="jo-xyz-demo-card__header">
                  <h3 className="jo-xyz-demo-card__title">Selected Offer Details</h3>
                  <p className="jo-xyz-demo-card__subtitle">
                    Review the details of your selected offer before accepting
                  </p>
                </div>
                <div className="jo-xyz-demo-offer-comparison">
                  {(() => {
                    const offer = availableOffers.find(o => o.id === selectedOffer);
                    if (!offer) return null;
                    
                    return (
                      <div className="jo-xyz-demo-comparison-details">
                        <div className="jo-xyz-demo-comparison-section">
                          <h4>Financial Terms</h4>
                          <div className="jo-xyz-demo-comparison-grid">
                            <div className="jo-xyz-demo-comparison-item">
                              <span className="jo-xyz-demo-comparison-item__label">Loan Amount</span>
                              <span className="jo-xyz-demo-comparison-item__value">{formatCurrency(offer.amount)}</span>
                            </div>
                            <div className="jo-xyz-demo-comparison-item">
                              <span className="jo-xyz-demo-comparison-item__label">Interest Rate</span>
                              <span className="jo-xyz-demo-comparison-item__value">{offer.interestRate}% APR</span>
                            </div>
                            <div className="jo-xyz-demo-comparison-item">
                              <span className="jo-xyz-demo-comparison-item__label">Monthly Payment</span>
                              <span className="jo-xyz-demo-comparison-item__value">
                                {formatCurrency(offer.amount * (offer.interestRate / 100 / 12))}
                              </span>
                            </div>
                            <div className="jo-xyz-demo-comparison-item">
                              <span className="jo-xyz-demo-comparison-item__label">Total Interest</span>
                              <span className="jo-xyz-demo-comparison-item__value">
                                {formatCurrency(offer.amount * (offer.interestRate / 100) * (offer.term / 12))}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="jo-xyz-demo-comparison-section">
                          <h4>All Conditions</h4>
                          <ul className="jo-xyz-demo-conditions-full-list">
                            {offer.conditions.map((condition, index) => (
                              <li key={index} className="jo-xyz-demo-condition-full-item">
                                {condition}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Offers;
